import * as THREE from 'three';
import type { BakedFrame, BakedModel, BakedPart } from './bake.ts';
import type { AttrData } from './lod-core.ts';
import type { FarJob, FarReply } from './lod.worker.ts';

/**
 * A far version of a baked model: the same vertices (and buffers), drawn through an index that keeps about a third
 * of the triangles (meshoptimizer's simplifier, its tiny pieces pruned). A crowd of nightmares seen from the game's
 * camera is ~50 px tall each, a few pixels per triangle: on a phone's GPU their cost goes by the triangles, not by
 * the pixels (a real wave on an Adreno 740: a third of the nightmares' triangles saves ~9 ms of a ~40 ms frame;
 * half the resolution saves ~3; an index that only reuses vertices, nothing). Zoomed in, the full model draws.
 *
 * Simplifying takes 10–70 ms a model on an Adreno 740: a worker does it (lod.worker.ts), and until its answer is
 * back the full model draws. Without workers (Node, tests, `?lodWorker=0`) it is done here, once the WebAssembly
 * is ready.
 */

/** `?lod=0`: always the full models, to compare side by side. */
export const LOD = { on: typeof location === 'undefined' || new URLSearchParams(location.search).get('lod') !== '0' };

/**
 * Below this many screen pixels per world unit where the camera looks (a nightmare is ~1.2 units tall), the far
 * version draws: a phone at the game's usual zoom shows ~47, a 1305 px tall desktop ~51; zoomed in, 90 and more.
 */
export const FAR_BELOW_PX = 55;

/** The simplifier on this thread, for when there is no worker (loaded then: the page's bundle doesn't carry it). */
let core: typeof import('./lod-core.ts') | null = null;
let loading: Promise<unknown> | null = null;
/** Settles once far versions can be made here (Node and tests; a page uses the worker). */
export const farReady = (): Promise<unknown> =>
  (loading ??= import('./lod-core.ts').then(async (c) => {
    await c.simplifierReady;
    core = c;
  }));

/** Each baked geometry's far view (the geometry itself when it can't shrink). */
const FAR_GEO = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();
const FAR_MODEL = new WeakMap<BakedModel, BakedModel>();
/** Models whose meshes are with the worker. */
const ASKED = new WeakSet<BakedModel>();

/** The geometry drawn through `ix` (over the same buffers), or itself without one. */
const view = (g: THREE.BufferGeometry, ix: Uint16Array | Uint32Array | null): THREE.BufferGeometry => {
  if (!ix) return g;
  const out = new THREE.BufferGeometry();
  for (const [k, a] of Object.entries(g.attributes)) out.setAttribute(k, a);
  out.setIndex(new THREE.BufferAttribute(ix, 1));
  out.boundingSphere = g.boundingSphere;
  out.boundingBox = g.boundingBox;
  // A nightmare's boil frames keep their atlas run by the full geometry (boil.ts): same vertices.
  out.userData.full = g;
  return out;
};

/** What the simplifier needs of a baked geometry (non-indexed, as bake.ts leaves it); null when it has an index. */
const job = (g: THREE.BufferGeometry): FarJob['parts'][number] | null => {
  if (g.index) return null;
  const pos = g.getAttribute('position');
  const n = pos.count;
  const xyz = pos.array instanceof Float32Array && pos.itemSize === 3 ? pos.array : Float32Array.from({ length: n * 3 }, (_, i) => pos.getComponent(Math.floor(i / 3), i % 3));
  const attrs: AttrData[] = Object.values(g.attributes).map((a) => ({ array: a.array as ArrayLike<number>, itemSize: a.itemSize }));
  return { attrs, pos: xyz, n };
};

const geometries = (m: BakedModel): THREE.BufferGeometry[] => [...new Set([...m.frames.flatMap((f) => f.parts), ...m.dynamic.flatMap((d) => d.frame.parts)].map((p) => p.geometry))];

let worker: Worker | null | undefined;
let nextId = 0;
const waiting = new Map<number, THREE.BufferGeometry[]>();

const start = (): Worker | null => {
  if (worker !== undefined) return worker;
  worker = null;
  if (typeof Worker === 'undefined' || typeof location === 'undefined' || /[?&]lodWorker=0/.test(location.search)) return null;
  try {
    const w = new Worker(new URL('./lod.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent<FarReply>): void => {
      const geos = waiting.get(e.data.id);
      waiting.delete(e.data.id);
      geos?.forEach((g, i) => FAR_GEO.set(g, view(g, e.data.index[i] ?? null)));
    };
    // A worker that can't start (or dies): what it had stays full.
    w.onerror = (): void => {
      worker = null;
      w.terminate();
    };
    worker = w;
  } catch {
    worker = null;
  }
  return worker;
};

/** Sends `model`'s meshes to the worker (once; the arrays are copied, the page keeps drawing them). */
const ask = (model: BakedModel, w: Worker): void => {
  if (ASKED.has(model)) return;
  ASKED.add(model);
  const parts: FarJob['parts'] = [];
  const sent: THREE.BufferGeometry[] = [];
  for (const g of geometries(model)) {
    if (FAR_GEO.has(g)) continue;
    const j = job(g);
    if (j) {
      parts.push(j);
      sent.push(g);
    } else FAR_GEO.set(g, g);
  }
  if (!sent.length) return;
  const id = nextId++;
  waiting.set(id, sent);
  w.postMessage({ id, parts } satisfies FarJob);
};

/** The far version of `model`, or null until it is made (draw the full one meanwhile). */
export const farModel = (model: BakedModel): BakedModel | null => {
  const known = FAR_MODEL.get(model);
  if (known) return known;
  const geos = geometries(model);
  if (!geos.every((g) => FAR_GEO.has(g))) {
    const w = start();
    if (w) {
      ask(model, w);
      return null;
    }
    const c = core;
    if (!c) {
      void farReady();
      return null;
    }
    for (const g of geos) {
      if (FAR_GEO.has(g)) continue;
      const j = job(g);
      FAR_GEO.set(g, view(g, j ? c.farIndex(j.attrs, j.pos, j.n) : null));
    }
  }
  const frame = (f: BakedFrame): BakedFrame => ({ parts: f.parts.map((p): BakedPart => ({ ...p, geometry: FAR_GEO.get(p.geometry) as THREE.BufferGeometry })), glows: f.glows });
  const frames = model.frames.map(frame);
  const dynamic = model.dynamic.map((d) => ({ ...d, frame: frame(d.frame) }));
  let triangles = 0;
  for (const p of [...(frames[0]?.parts ?? []), ...dynamic.flatMap((d) => d.frame.parts)]) triangles += (p.geometry.index?.count ?? p.geometry.getAttribute('position').count) / 3;
  const far = { ...model, frames, dynamic, triangles };
  FAR_MODEL.set(model, far);
  return far;
};

const INDEXED = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();

/** `g` drawn through an index (its own, or 0, 1, 2… over the same buffers): for merges that need every piece indexed. */
export const withIndex = (g: THREE.BufferGeometry): THREE.BufferGeometry => {
  if (g.index) return g;
  let out = INDEXED.get(g);
  if (!out) {
    const n = g.getAttribute('position').count;
    const ix = n < 65536 ? new Uint16Array(n) : new Uint32Array(n);
    for (let i = 0; i < n; i++) ix[i] = i;
    out = view(g, ix);
    INDEXED.set(g, out);
  }
  return out;
};
